import { computeText } from '@/locales/utils';
import { formatDate } from '@/utils/date';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TableColumn, TableRow, TableCell } from '@/components/atoms/Table/Table.type';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, fetchDataProps } from '../../components/atoms/Table/Table';
import { redirectToBackofficeV1 } from '@/utils/backoffice-v1';
import { Cell } from 'react-table';
import { convertTableFiltersToQueryFilters } from '../Order/OrderList/utils';
import { useLazyGetRestaurantListV2Query } from '@/services/innovorder/restaurant/restaurant.endpoints';
import {
    GetRestaurantListV2Payload,
    GetRestaurantListV2Response,
} from '@/services/innovorder/restaurant/restaurant.type';
interface RestaurantListViewModel {
    rows: TableRow[];
    columns: TableColumn[];
    isLoading: boolean;
    data: GetRestaurantListV2Response | undefined;
    handleRowClick: (
        cell: Cell<TableRow, TableCell<unknown>> | undefined,
        event?: React.MouseEvent<HTMLTableCellElement>,
    ) => void;
    fetchData: (fetchProps: fetchDataProps) => void;
}

const useRestaurantListVM = (): RestaurantListViewModel => {
    const intl = useIntl();
    const [fetchRestaurantList, { data, isLoading }] = useLazyGetRestaurantListV2Query();
    const [fetchParams, setFetchParams] = useState<GetRestaurantListV2Payload | null>(null);

    const fetchData = useCallback(
        ({ pageIndex, pageSize, globalFilter, columnFilters, sortBy }: fetchDataProps) => {
            const filter = convertTableFiltersToQueryFilters(columnFilters);
            const latestFetchParams: GetRestaurantListV2Payload = {
                limit: pageSize ?? DEFAULT_PAGE_SIZE,
                offset: pageIndex === undefined || pageSize === undefined ? DEFAULT_PAGE_INDEX : pageIndex * pageSize,
                search: globalFilter,
                filter,
            };
            if (sortBy?.[0]) {
                latestFetchParams.sortBy = sortBy?.[0]?.id;
                latestFetchParams.sortDirection = sortBy?.[0]?.desc ? 'desc' : 'asc';
            }
            setFetchParams(latestFetchParams);
            fetchRestaurantList(latestFetchParams);
        },
        [fetchRestaurantList],
    );

    const handleRowClick = useCallback(
        (cell: Cell<TableRow, TableCell<unknown>> | undefined, event?: React.MouseEvent<HTMLTableCellElement>) => {
            if (!cell) {
                return;
            }

            const brandId = data?.restaurants.find(
                (restaurant) => restaurant.restaurantId === cell?.row.values.restaurantId.value.text,
            )?.brandId;
            redirectToBackofficeV1(
                `/home/brands/${brandId}/restaurants/${cell?.row.values.restaurantId.value.text}/info`,
                event?.ctrlKey,
            );
        },
        [data?.restaurants],
    );
    // @ts-ignore
    const columns: TableColumn[] = useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'restaurantId',
                disableFilters: true,
                disableSortBy: false,
            },
            {
                Header: computeText(intl, 'name.name'),
                accessor: 'name',
                disableFilters: true,
                disableSortBy: false,
            },
            {
                Header: computeText(intl, 'date.created'),
                accessor: 'createdAt',
                disableFilters: true,
                disableSortBy: false,
            },
            {
                Header: 'Brand',
                accessor: 'brandName',
                disableFilters: true,
                disableSortBy: false,
            },
        ],
        [intl],
    );

    const rows: TableRow[] = useMemo(() => {
        if (!data) return [];
        return data.restaurants.map((restaurant) => ({
            restaurantId: {
                type: 'string',
                value: {
                    text: restaurant.restaurantId,
                },
            },
            name: {
                type: 'string',
                value: {
                    text: restaurant.name,
                },
            },
            createdAt: {
                type: 'string',
                value: {
                    text: formatDate(restaurant.createdAt, 'DD/MM/YYYY HH:mm'),
                },
            },
            brandName: {
                type: 'string',
                value: {
                    text: restaurant.brandName,
                },
            },
        }));
    }, [data]);
    return { rows, columns, handleRowClick, fetchData, data, isLoading };
};
export default useRestaurantListVM;
